import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { BRAND_CONFIG } from 'constants/branding';
import { partyNamesMatch } from './party';

/**
 * The privacy policy, served by the portal itself.
 *
 * <h3>Why it lives here</h3>
 * The login screen has linked to {@code /privacy} since the brand layer was written
 * and no route ever answered it, so the link opened a blank tab on every brand. It
 * also has to exist somewhere we control: a connector directory listing requires a
 * privacy policy URL, and an incomplete one is an immediate rejection.
 *
 * <h3>Why it is branded rather than one page</h3>
 * The portal is resold. A CatchMyCall customer reading a policy signed by another
 * company has been told their data is held by a party they have no relationship
 * with. The text is shared, because it is one product; the legal name, contact
 * address and postal address come from {@link BRAND_CONFIG}, so adding a reseller
 * stays a config entry.
 *
 * <h3>Public on purpose</h3>
 * Linked from the login screen, so it cannot sit behind PrivateRoute — a reader who
 * has not signed in is exactly who needs it.
 *
 * <p><b>The content is factual about the product, not legal advice.</b> Retention
 * periods and the processor list are the ones the platform actually operates; they
 * need confirming against the contracts before this is cited in a submission.
 */

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="mt-8">
    <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
    <div className="mt-2 space-y-3 text-sm leading-relaxed text-slate-700">{children}</div>
  </section>
);

const Privacy: React.FC = () => {
  const { brandName, privacy } = BRAND_CONFIG;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
        <Link
          to="/login"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-700 hover:text-blue-800"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Back to sign in
        </Link>

        <header className="mt-6 border-b border-slate-200 pb-6">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Privacy Policy</h1>
          <p className="mt-2 text-sm text-slate-600">
            How {brandName} handles information in this platform.
          </p>
          <p className="mt-1 text-xs text-slate-500">Last updated {privacy.lastUpdated}</p>
        </header>

        <Section title="1. Who this policy is from">
          <p>
            This platform is provided by {privacy.legalName} (&ldquo;we&rdquo;)
            {partyNamesMatch() ? '' : `, operating as ${brandName}`}. It covers the {brandName}{' '}
            platform and the telephony and messaging services delivered through it.
          </p>
          <p>
            If you are an end customer of a business that uses {brandName} to handle its calls or
            messages, that business decides what it collects and why. We process that information
            on its behalf, and its own privacy policy governs it.
          </p>
        </Section>

        <Section title="2. What we collect">
          <p>
            <strong>Account information.</strong> Your name, work email address, phone number,
            organisation, role and the audit record of actions taken in the platform.
          </p>
          <p>
            <strong>Call data.</strong> Caller and destination numbers, the date, time and duration
            of each call, how it was routed, and its outcome.
          </p>
          <p>
            <strong>Call recordings and transcripts</strong>, where recording is enabled for your
            organisation. Recordings contain whatever was said on the call.
          </p>
          <p>
            <strong>Messaging data.</strong> For WhatsApp, SMS and email: message content, recipient
            addresses or numbers, the templates you create, and delivery metadata — sent, delivered,
            read and failure status, with the time of each.
          </p>
          <p>
            <strong>Contacts and leads</strong> you upload or that are captured from conversations,
            including the fields your organisation chooses to record.
          </p>
          <p>
            <strong>Technical data.</strong> IP address, browser and device information, and
            platform logs needed to operate and secure the service.
          </p>
        </Section>

        <Section title="3. How we use it">
          <p>
            To place, receive and route calls; to send the messages and campaigns you initiate; to
            show you call history, delivery status and campaign results; to transcribe and summarise
            calls and extract lead details where your organisation has enabled it; to bill for usage;
            to provide support; and to keep the service secure and working.
          </p>
          <p>
            We do not sell your information, and we do not use the contents of your calls or messages
            to train models for anyone else.
          </p>
        </Section>

        <Section title="4. Automated processing">
          <p>
            Where your organisation enables it, calls may be answered or assisted by an automated
            agent, and call audio may be transcribed and summarised to produce call notes and lead
            records. This is applied to your organisation&rsquo;s own calls to produce your own
            records.
          </p>
        </Section>

        <Section title="5. Connecting an AI assistant">
          <p>
            You can choose to connect a third-party AI assistant, such as Claude or ChatGPT, to your
            account. This is optional and nothing is connected unless you set it up yourself.
          </p>
          <p>
            Before you approve a connection you are shown exactly what it is asking for, and you
            choose which of those permissions to grant. Depending on what you grant, the assistant
            can read your call history and campaign results, send WhatsApp messages, and place calls
            to numbers you provide.{' '}
            <strong>
              Calls and messages it sends are real, reach real people and are charged to your account
              like any other.
            </strong>
          </p>
          <p>
            We never give the assistant your password. Approving a connection issues a separate,
            time-limited credential restricted to the permissions you granted, and the assistant
            provider receives only what is needed for the requests you ask it to make. Anything you
            ask an assistant is also subject to that provider&rsquo;s own privacy policy.
          </p>
          <p>
            You can revoke a connection at any time, which stops the assistant reaching your account.
            Revoking does not delete information already processed; the retention periods below
            continue to apply.
          </p>
        </Section>

        <Section title="6. Who we share it with">
          <p>
            <strong>Telecommunications carriers</strong>, to connect calls and deliver SMS — they
            receive the numbers involved and the routing details needed to carry the traffic.
          </p>
          <p>
            <strong>Meta Platforms</strong>, for WhatsApp. Message content, recipient numbers and
            delivery metadata are shared in order to send them, under Meta&rsquo;s own terms.
          </p>
          <p>
            <strong>Cloud hosting, transcription and AI providers</strong>, who process data on our
            instructions to run the service.
          </p>
          <p>
            <strong>Payment processors</strong>, for billing.
          </p>
          <p>
            And where we are required to by law. We do not share your information with anyone else
            without your instruction.
          </p>
        </Section>

        <Section title="7. How long we keep it">
          <p>
            Account and organisation records are kept for as long as the account is open, and for a
            limited period after it closes so that billing and audit obligations can be met. Call
            logs, message logs, contacts and leads are kept while your organisation needs them in the
            platform. Call recordings are kept for the retention period configured for your
            organisation, after which they are deleted.
          </p>
          <p>
            Your organisation&rsquo;s administrator can request deletion of its data, and we delete
            it except where we are required to retain something by law.
          </p>
        </Section>

        <Section title="8. Security">
          <p>
            Access is restricted by role, data is encrypted in transit, and credentials are stored
            hashed. Administrative access to production is limited and logged. No system is perfectly
            secure, and we will tell you promptly if a breach affects your information.
          </p>
        </Section>

        <Section title="9. Your choices">
          <p>
            You can ask us for a copy of the information we hold about you, ask us to correct it, or
            ask us to delete it. If you are a user within an organisation, start with your
            organisation&rsquo;s administrator, who controls most of these settings directly. Write to
            us at the address below and we will respond.
          </p>
        </Section>

        <Section title="10. Changes">
          <p>
            If we change this policy we will update the date at the top of this page, and we will tell
            you in the platform if a change materially affects how your information is handled.
          </p>
        </Section>

        <Section title="11. Contact us">
          <p>
            Privacy questions go to{' '}
            <a
              className="font-medium text-blue-700 hover:text-blue-800"
              href={`mailto:${privacy.contactEmail}`}
            >
              {privacy.contactEmail}
            </a>
            .
          </p>
          <p>
            {privacy.legalName}
            <br />
            {privacy.address}
          </p>
        </Section>

        <footer className="mt-10 border-t border-slate-200 pt-6 text-xs text-slate-500">
          {privacy.legalName}
        </footer>
      </div>
    </div>
  );
};

export default Privacy;
